import { TokenResponse } from 'expo-auth-session';
import { Platform } from 'react-native';

import { API_BASE_URL, joinApiUrl } from '@/services/api-base-url';
import { deviceTokenService } from '@/services/device-token-service';
import { fetchWithTimeout } from '@/services/http-service';
import { setNotificationHistoryOwner } from '@/services/notification-history';
import { secureStoreService } from '@/services/secure-store-service';
import type { User } from '@/types/models';
import { isDevBuild, isDeviceTokenShape, isLocalApiUrl } from '@/utils/dev-sign-in';
import { logger } from '@/utils/logger';

export type UserSession = {
	id: string;
	name: string;
	email: string;
	accessToken?: string;
	idToken?: string;
	refreshToken?: string;
	deviceToken?: string;
	provider?: 'google' | 'azure' | 'apple';
	isDemoMode?: boolean;
};

export const AZURE_INFO_URL = 'https://graph.microsoft.com/v1.0/me';
export const AZURE_REFRESH_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/token';
export const AZURE_CLIENT_ID = process.env.EXPO_PUBLIC_AZURE_CLIENT_ID || 'e58f2456-b28a-4958-aada-1967f5c2b5ad';
export const AZURE_REDIRECT_URI = Platform.select({
	ios: process.env.EXPO_PUBLIC_AUTH_REDIRECT_URI_IOS || 'msauth.workorder://auth',
	web: globalThis?.location ? `${globalThis.location.origin}/auth` : process.env.EXPO_PUBLIC_AUTH_REDIRECT_URI_WEB || 'https://sbs.propertyworkorder.app/auth',
	default: process.env.EXPO_PUBLIC_AUTH_REDIRECT_URI || 'workorder://auth',
}) || 'workorder://auth';
logger.log('AZURE_REDIRECT_URI', AZURE_REDIRECT_URI);

/**
 * api-service imports this module and subscribes to `authService` while it loads, so a static
 * import back is a cycle: whichever entry reaches auth-service first (the server render of a
 * print route) hits `authService` in its TDZ. Resolve it on use instead.
 */
const loadApiService = async () => (await import('@/services/api-service')).apiService;

export class UnauthorizedError extends Error {
	constructor(message: string = 'Unauthorized') {
		super(message);
		this.name = 'UnauthorizedError';
	}
}

class AuthService {
	private currentSession: UserSession | null = null;
	private currentUser: User | null = null;
	private sessionListeners: ((session: UserSession | null) => void)[] = [];
	private userListeners: ((user: User | null) => void)[] = [];
	private errorListeners: ((error: string | null) => void)[] = [];
	private authError: string | null = null;
	private isInitialized = false;
	private initPromise: Promise<void> | null = null;
	private originalAdminSession: UserSession | null = null;
	/**
	 * Bumped — synchronously, before any await — whenever the current session's lifetime ends or
	 * its credentials change hands: sign-out begins, a different account signs in, a stored
	 * session is restored, impersonation starts or ends. A token operation captures it up front
	 * and may only write its result back while it is unchanged (`patchSessionIfStill`). An id
	 * comparison is not enough: sign-out keeps `currentSession` until its storage deletes finish,
	 * so a refresh landing in that window still "matches" (Codex review round 4 on #693).
	 */
	private sessionEpoch = 0;
	/**
	 * The lifetime each published session object belongs to. A request is sent with a session OBJECT,
	 * so this is how a late 401 is tied to the lifetime that sent it — the user id is not enough once
	 * the same account signs out and back in (Codex review round 7 on #693).
	 */
	private sessionEpochs = new WeakMap<UserSession, number>();

	constructor() {
		this.initPromise = this.initialize();
	}

	private async initialize() {
		if (this.isInitialized) return;

		logger.log('AuthService: Starting initialization');
		const storageInfo = secureStoreService.getStorageInfo();
		logger.log('AuthService storage info:', storageInfo);

		try {
			await this.loadStoredSession();
			await this.loadStoredUser();
			this.isInitialized = true;
			logger.log('AuthService: Initialization complete', {
				hasSession: !!this.currentSession,
				hasUser: !!this.currentUser,
				sessionEmail: this.currentSession?.email,
			});
		} catch (error) {
			console.error('AuthService: Initialization failed:', error);
			this.isInitialized = true; // Mark as initialized even on error to prevent hanging
		}
	}

	async waitForInitialization(): Promise<void> {
		if (this.initPromise) {
			await this.initPromise;
		}
	}

	async getSession(): Promise<UserSession | null> {
		await this.waitForInitialization();
		return this.currentSession;
	}

	async getUser(): Promise<User | null> {
		await this.waitForInitialization();
		return this.currentUser;
	}

	getAuthError(): string | null {
		return this.authError;
	}

	setAuthError(error: string | null) {
		this.authError = error;
		this.notifyErrorListeners();
	}

	// Event listeners
	onSessionChange(callback: (session: UserSession | null) => void): () => void {
		this.sessionListeners.push(callback);
		callback(this.currentSession); // Call immediately with current session
		return () => {
			this.sessionListeners = this.sessionListeners.filter(cb => cb !== callback);
		};
	}

	onUserChange(callback: (user: User | null) => void): () => void {
		this.userListeners.push(callback);
		callback(this.currentUser); // Call immediately with current user
		return () => {
			this.userListeners = this.userListeners.filter(cb => cb !== callback);
		};
	}

	onAuthError(callback: (error: string | null) => void): () => void {
		this.errorListeners.push(callback);
		callback(this.authError); // Call immediately with current error
		return () => {
			this.errorListeners = this.errorListeners.filter(cb => cb !== callback);
		};
	}

	private notifySessionListeners() {
		for (const listener of this.sessionListeners) {
			listener(this.currentSession);
		}
	}

	private notifyUserListeners() {
		for (const listener of this.userListeners) {
			listener(this.currentUser);
		}
	}

	private notifyErrorListeners() {
		for (const listener of this.errorListeners) {
			listener(this.authError);
		}
	}

	// Session management
	async signIn(tokens?: TokenResponse): Promise<UserSession | null> {
		logger.log('AuthService.signIn', tokens);

		if (tokens) {
			try {
				await secureStoreService.setItemAsync('authTokens', JSON.stringify(tokens));
				logger.log('AuthService: Tokens stored securely');
			} catch (error) {
				console.warn('Could not store auth tokens securely:', error);
			}

			return this.signInWithTokenResponse(tokens);
		}

		return null;
	}

	async signInWithTokenResponse(response: TokenResponse): Promise<UserSession> {
		logger.log('Processing token response:', response);

		let userInfo;
		if (response.accessToken) {
			try {
				const userInfoResponse = await fetchWithTimeout(AZURE_INFO_URL, {
					headers: {
						Authorization: `Bearer ${response.accessToken}`,
					},
				});

				if (userInfoResponse.ok) {
					userInfo = await userInfoResponse.json();
					logger.log('User info from Microsoft Graph:', userInfo);
				} else {
					console.error('Failed to fetch user info from Microsoft Graph');
				}
			} catch (error) {
				console.error('Error fetching user info:', error);
			}
		}

		const session: UserSession = {
			id: userInfo?.id || response.idToken || 'unknown',
			name: userInfo?.displayName || userInfo?.name || 'Unknown User',
			email: userInfo?.mail || userInfo?.userPrincipalName || userInfo?.email || 'no-email@example.com',
			accessToken: response.accessToken || undefined,
			idToken: response.idToken || undefined,
			refreshToken: response.refreshToken || undefined,
			provider: 'azure',
		};

		await this.setSession(session);

		// Generate and register device token for persistent authentication
		logger.log('Generating device token for persistent authentication...');
		const deviceToken = await this.generateAndRegisterDeviceToken();
		if (deviceToken) {
			logger.log('Device token generated successfully');
		} else {
			console.warn('Failed to generate device token, session will not persist');
		}

		return session;
	}

	async signInWithApple(identityToken: string, user?: { fullName?: { givenName?: string; familyName?: string } | null; email?: string } | null): Promise<UserSession> {
		logger.log('Processing Apple Sign In');

		try {
			// Decode the identity token to get user info
			const payload = this.decodeJWT(identityToken);

			if (!payload || !payload.sub) {
				throw new Error('Invalid Apple identity token: missing payload or subject');
			}

			// Validate token expiration
			const now = Math.floor(Date.now() / 1000);
			if (payload.exp && payload.exp < now) {
				throw new Error('Apple identity token has expired');
			}

			// Validate issuer
			if (payload.iss !== 'https://appleid.apple.com') {
				throw new Error('Invalid Apple identity token: wrong issuer');
			}

			const session: UserSession = {
				id: payload.sub,
				name: user?.fullName ? `${user.fullName.givenName || ''} ${user.fullName.familyName || ''}`.trim() : 'Apple User',
				email: user?.email || payload.email || 'apple-user@privaterelay.appleid.com',
				idToken: identityToken,
				provider: 'apple',
			};

			logger.log('Apple Sign In session created:', { id: session.id, email: session.email });

			const isRegistered = await this.isAppleUserRegistered(identityToken);
			if (!isRegistered) {
				logger.log('Apple Sign In: user not registered, entering demo mode');
				await deviceTokenService.clearDeviceToken();
				const demoSession: UserSession = {
					...session,
					isDemoMode: true,
				};
				await this.setSession(demoSession);
				return demoSession;
			}

			await this.setSession(session);

			// Generate and register device token for persistent authentication
			logger.log('Generating device token for persistent authentication...');
			const deviceToken = await this.generateAndRegisterDeviceToken();
			if (deviceToken) {
				logger.log('Device token generated successfully');
			} else {
				console.warn('Failed to generate device token, session will not persist');
			}

			return session;
		} catch (error) {
			console.error('Error processing Apple Sign In:', error);
			throw new Error(`Apple Sign In failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
		}
	}

	/**
	 * Enter demo mode directly without going through a provider. Used for
	 * App Store screenshot automation and for the "Try Demo" entry point on
	 * the sign-in screen (same user experience the App Review reviewer sees
	 * after signing in with an unprovisioned Apple ID).
	 */
	async enterDemoMode(): Promise<UserSession> {
		await deviceTokenService.clearDeviceToken();
		// Silence LogBox so yellow/red dev-build warnings don't overlap App
		// Store screenshots or appear to an App Review reviewer exploring demo.
		try {
			const { LogBox } = await import('react-native');
			LogBox.ignoreAllLogs(true);
		} catch {
			// react-native LogBox isn't available on web — ignore
		}
		const demoSession: UserSession = {
			id: `demo-${Date.now()}`,
			name: 'Demo User',
			email: 'demo@propertyworkorder.app',
			provider: 'apple',
			isDemoMode: true,
		};
		await this.setSession(demoSession);
		return demoSession;
	}

	private async isAppleUserRegistered(identityToken: string): Promise<boolean> {
		try {
			const response = await fetchWithTimeout(joinApiUrl(API_BASE_URL, 'me'), {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${identityToken} apple`,
				},
			});

			if (response.ok) {
				return true;
			}

			// Only treat explicit auth/user-not-found responses as "unregistered" (demo mode).
			// Network errors or server errors should NOT silently drop a real user into demo mode.
			if (response.status === 401 || response.status === 403 || response.status === 404) {
				console.warn('Apple Sign In registration check: user not registered', response.status);
				return false;
			}

			const text = await response.text().catch(() => '');
			throw new Error(`Apple Sign In registration check failed (${response.status})${text ? `: ${text}` : ''}`);
		} catch (error) {
			console.warn('Apple Sign In registration check error:', error);
			throw error;
		}
	}

	private decodeJWT(token: string): any {
		try {
			if (!token || typeof token !== 'string') {
				throw new Error('Invalid token: not a string');
			}

			const parts = token.split('.');
			if (parts.length !== 3) {
				throw new Error('Invalid JWT format: must have 3 parts');
			}

			const base64Url = parts[1];
			if (!base64Url) {
				throw new Error('Invalid JWT format: missing payload');
			}

			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			}).join(''));

			const payload = JSON.parse(jsonPayload);
			logger.log('JWT payload decoded successfully:', { sub: payload.sub, iss: payload.iss, exp: payload.exp });
			return payload;
		} catch (error) {
			console.error('Error decoding JWT:', error);
			throw new Error(`JWT decode failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
		}
	}

	async refreshTokens(): Promise<UserSession | null> {
		const session = this.currentSession;
		const epoch = this.sessionEpoch;
		if (!session?.refreshToken) {
			logger.log('No refresh token available');
			return null;
		}

		try {
			const response = await fetchWithTimeout(AZURE_REFRESH_URL, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: new URLSearchParams({
					grant_type: 'refresh_token',
					client_id: AZURE_CLIENT_ID,
					refresh_token: session.refreshToken,
				}).toString(),
			});

			if (response.ok) {
				const tokenData = await response.json();
				logger.log('Token refresh successful');

				return await this.patchSessionIfStill(epoch, {
					accessToken: tokenData.access_token,
					refreshToken: tokenData.refresh_token || session.refreshToken,
				});
			} else {
				console.error('Token refresh failed:', response.status);
				return null;
			}
		} catch (error) {
			console.error('Error refreshing tokens:', error);
			return null;
		}
	}

	/**
	 * Silently renew a session the API has definitively refused, without asking the user.
	 *
	 * Refreshes the Azure token, then replaces the device token the API refused with a newly
	 * registered one. The refused token is dropped from the session FIRST, so if registration fails the session
	 * still works on the fresh OAuth token instead of re-sending the dead one (#692).
	 *
	 * @param refused the session object the refused request was sent with. Renewal only proceeds while
	 *   its LIFETIME is still live: a 401 that lands after a sign-out or account switch — even one where
	 *   the same account has signed back in — must never renew, and so replay the request, as the session
	 *   signed in now.
	 * @returns the renewed session, or `null` when it cannot be renewed silently — no refresh
	 *   token, the refresh was refused, the signed-in identity changed, or an impersonation is
	 *   active (renewing would swap the assumed identity's token for the admin's own).
	 */
	async reauthenticate(refused: UserSession): Promise<UserSession | null> {
		const session = this.currentSession;
		if (!session || !this.isSessionLive(refused) || session.isDemoMode || !session.refreshToken || this.isImpersonating()) return null;

		// Pinned to THIS session's lifetime: a sign-out or account switch during any await below
		// bumps the epoch, and every later step then refuses (`patchSessionIfStill`).
		const epoch = this.sessionEpoch;
		const refreshed = await this.refreshTokens();
		if (!refreshed || !this.isCurrentEpoch(epoch)) return null;
		if (!refreshed.deviceToken) return refreshed;

		if (!(await this.patchSessionIfStill(epoch, { deviceToken: undefined }))) return null;
		if (!this.isCurrentEpoch(epoch)) return null;
		await deviceTokenService.clearDeviceToken();
		await this.generateAndRegisterDeviceToken();
		return this.isCurrentEpoch(epoch) ? this.currentSession : null;
	}

	async signOut(): Promise<void> {
		logger.log('Signing out user');
		// First, before any await: no in-flight token operation may write this session back, and none
		// may START against it — a renewal begun mid-sign-out would capture the new epoch and pass.
		this.replaceSession(null);
		this.currentUser = null;
		this.authError = null;

		// Clear stored session, user, and auth tokens
		await secureStoreService.deleteItemAsync('userSession');
		await secureStoreService.deleteItemAsync('currentUser');
		await secureStoreService.deleteItemAsync('authTokens');

		// Clear device token
		await deviceTokenService.clearDeviceToken();

		// Persisted UI state outlives sign-out unless it is cleared here: the authenticated shell
		// UNMOUNTS on sign-out, so its own "owner changed" effect never sees a null user. This drops
		// the toast history from memory AND storage before the next person can sign in.
		await setNotificationHistoryOwner(null);

		// Notify listeners
		this.notifySessionListeners();
		this.notifyUserListeners();
		this.notifyErrorListeners();
	}

	/**
	 * Generate and register a device token for persistent authentication
	 */
	async generateAndRegisterDeviceToken(): Promise<string | null> {
		const session = this.currentSession;
		const epoch = this.sessionEpoch;
		if (!session?.accessToken && !session?.idToken) {
			console.error('No valid OAuth token available for device token registration');
			return null;
		}

		try {
			// Generate device token
			const deviceToken = await deviceTokenService.generateAndStoreDeviceToken();
			const deviceInfo = deviceTokenService.getDeviceInfo();

			// Register device token with backend using OAuth token
			// Default to 'microsoft' since that's the only OAuth provider the app supports
			const provider = session.provider || 'microsoft';
			const response = await fetchWithTimeout(joinApiUrl(API_BASE_URL, 'device-tokens'), {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${session.accessToken || session.idToken} ${provider}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					deviceToken,
					deviceName: deviceInfo.deviceName,
					devicePlatform: deviceInfo.devicePlatform,
					userAgent: deviceInfo.userAgent,
				}),
			});

			if (response.ok) {
				logger.log('Device token registered successfully');

				// The token belongs to whoever registered it — never attach it to an account signed in since.
				return (await this.patchSessionIfStill(epoch, { deviceToken })) ? deviceToken : null;
			} else {
				const errorText = await response.text();
				console.error('Device token registration failed:', response.status, errorText);
				// Clean up the local token if registration failed — unless it is now another account's.
				if (this.isCurrentEpoch(epoch)) await deviceTokenService.clearDeviceToken();
				return null;
			}
		} catch (error) {
			console.error('Error generating and registering device token:', error);
			if (this.isCurrentEpoch(epoch)) await deviceTokenService.clearDeviceToken();
			return null;
		}
	}

	/** True while the session a token operation started under (`epoch`) is still the live one. */
	private isCurrentEpoch(epoch: number): boolean {
		return !!this.currentSession && this.sessionEpoch === epoch;
	}

	/**
	 * Merge `patch` into the session — but only if it is still the session the operation started
	 * under (`epoch`, captured before its first await).
	 *
	 * Every read-modify-write of the session that spans an `await` (a token refresh, a device
	 * registration) must go through here: the user can sign out or switch accounts while the
	 * network call is in flight, and spreading `currentSession` afterwards would graft one
	 * account's tokens onto another's session, or resurrect a signed-out one (Codex review
	 * rounds 1–4 on #693).
	 *
	 * The check and the write are one synchronous step: `setSession` assigns before its first
	 * `await` when the id is unchanged, so nothing can interleave between them.
	 *
	 * @returns the updated session, or `null` when the session moved on and the patch was dropped.
	 */
	private async patchSessionIfStill(epoch: number, patch: Partial<UserSession>): Promise<UserSession | null> {
		if (!this.currentSession || !this.isCurrentEpoch(epoch)) {
			logger.log('Session changed while a token operation was in flight; discarding its result.');
			return null;
		}
		const updated: UserSession = { ...this.currentSession, ...patch };
		await this.setSession(updated);
		return updated;
	}

	// User management
	async setUser(user: User | null) {
		this.currentUser = user;

		// Store user in secure storage
		if (user) {
			await secureStoreService.setItemAsync('currentUser', JSON.stringify(user));
		} else {
			await secureStoreService.deleteItemAsync('currentUser');
		}

		this.notifyUserListeners();
	}

	/**
	 * Hand the in-memory session to `session` (or to nobody) AND retire every token operation started
	 * under the old one — as ONE synchronous step. The ONLY way a session may change hands.
	 *
	 * Splitting the two is the bug Codex found in rounds 5 and 6 on #693: bump first and swap after an
	 * await, and an operation starting in between pairs the OLD session's tokens with the NEW epoch,
	 * so its result is later accepted into the new session. Swap first and bump later, and one already
	 * in flight writes over the new session.
	 */
	private replaceSession(session: UserSession | null): void {
		this.sessionEpoch += 1;
		this.currentSession = session;
		if (session) this.sessionEpochs.set(session, this.sessionEpoch);
	}

	/**
	 * Whether `session` — the object a request was sent with — belongs to the session lifetime that is
	 * live now. False once that lifetime ended (sign-out, account switch, impersonation change), even if
	 * the same account has signed back in since.
	 */
	isSessionLive(session: UserSession): boolean {
		return !!this.currentSession && this.sessionEpochs.get(session) === this.sessionEpoch;
	}

	/** The lifetime `session` was published under, or `undefined` for an object this service never published. */
	sessionEpochOf(session: UserSession): number | undefined {
		return this.sessionEpochs.get(session);
	}

	// Private methods
	private async setSession(session: UserSession) {
		const switchingFrom = this.currentSession?.id && this.currentSession.id !== session.id ? this.currentSession.id : null;

		// A new identity (sign-in, account switch) is a hand-over; a token patch of the same one is not.
		if (this.currentSession?.id !== session.id) this.replaceSession(session);
		else {
			// Same lifetime, refreshed tokens: requests sent with this object belong to it too.
			this.sessionEpochs.set(session, this.sessionEpoch);
			this.currentSession = session;
		}

		// If switching users, clear any previously-stored device token to prevent cross-user auth leakage.
		if (switchingFrom) {
			await deviceTokenService.clearDeviceToken();
		}

		// Store session in secure storage
		await secureStoreService.setItemAsync('userSession', JSON.stringify(session));

		// Clear any auth errors
		this.authError = null;

		// Notify listeners
		this.notifySessionListeners();
		this.notifyErrorListeners();

		logger.log('Session updated:', {
			id: session.id,
			email: session.email,
			provider: session.provider,
			hasAccessToken: !!session.accessToken,
			hasIdToken: !!session.idToken,
		});
	}

	private async loadStoredSession() {
		try {


			const storedSession = await secureStoreService.getItemAsync('userSession');
			if (storedSession) {
				this.replaceSession(JSON.parse(storedSession));
				logger.log('Loaded stored session:', {
					id: this.currentSession?.id,
					email: this.currentSession?.email,
					provider: this.currentSession?.provider,
					hasAccessToken: !!this.currentSession?.accessToken,
					hasRefreshToken: !!this.currentSession?.refreshToken,
					hasDeviceToken: !!this.currentSession?.deviceToken,
				});

				// Check for device token and load if available
				const storedDeviceToken = await deviceTokenService.getDeviceToken();
				if (storedDeviceToken && this.currentSession && !this.currentSession.deviceToken) {
					logger.log('Found stored device token, adding to session');
					this.currentSession.deviceToken = storedDeviceToken;
					// Update stored session with device token
					await secureStoreService.setItemAsync('userSession', JSON.stringify(this.currentSession));
				}

				// For device token authentication, skip OAuth token validation
				if (this.currentSession?.deviceToken) {
					logger.log('Device token available, will use for authentication');
					return;
				}

				// For Apple auth, we don't need to validate tokens
				if (this.currentSession?.provider === 'apple') {
					logger.log('Apple session loaded, skipping token validation');
					return;
				}

				// Validate token if available (but don't fail session loading if validation fails)
				if (this.currentSession?.accessToken) {
					try {
						await this.validateToken();
					} catch (error) {
						console.warn('Token validation failed during session load, but keeping session:', error);
						// Don't clear the session here - let it try to work and only clear if API calls fail
					}
				}
			} else {
				logger.log('No stored session found');
			}
		} catch (error) {
			console.error('Error loading stored session:', error);
			// Clear potentially corrupted session data
			await secureStoreService.deleteItemAsync('userSession');
		}
	}

	private async loadStoredUser() {
		try {


			const storedUser = await secureStoreService.getItemAsync('currentUser');
			if (storedUser) {
				this.currentUser = JSON.parse(storedUser);
				logger.log('Loaded stored user:', {
					id: this.currentUser?.id,
					email: this.currentUser?.email,
					name: this.currentUser?.name,
				});
			}
		} catch (error) {
			console.error('Error loading stored user:', error);
		}
	}

	private async validateToken(): Promise<boolean> {
		if (!this.currentSession?.accessToken) {
			logger.log('No access token to validate');
			return false;
		}

		try {
			logger.log('Validating access token...');
			const response = await fetchWithTimeout(AZURE_INFO_URL, {
				headers: {
					Authorization: `Bearer ${this.currentSession.accessToken}`,
				},
			});

			if (!response.ok) {
				logger.log('Token validation failed with status:', response.status);

				// Only try to refresh if we have a refresh token
				if (this.currentSession?.refreshToken) {
					logger.log('Attempting token refresh...');
					const refreshedSession = await this.refreshTokens();
					if (refreshedSession) {
						logger.log('Token refresh successful');
						return true;
					} else {
						console.warn('Token refresh failed');
						return false;
					}
				} else {
					console.warn('No refresh token available');
					return false;
				}
			} else {
				logger.log('Token validation successful');
				return true;
			}
		} catch (error) {
			console.error('Error during token validation:', error);
			return false;
		}
	}

	/**
	 * DEV ONLY: sign in with an existing device token against a LOCAL API (`app/dev/sign-in.tsx`).
	 *
	 * Re-checks every guard the route applies, so no other caller can reach a shared API with a
	 * pasted token. Identity comes from the API's own `/me` for that token — never from the
	 * link — and the session goes through `setSession`, the same path as every other sign-in, so
	 * listeners (and so `apiService`'s user load) fire without an app restart.
	 */
	async signInWithDeviceTokenForDev(token: string): Promise<void> {
		if (!isDevBuild()) throw new Error('Dev sign-in is only available in development builds');
		const baseUrl = (await loadApiService()).getApiBaseUrl();
		if (!isLocalApiUrl(baseUrl)) throw new Error('Dev sign-in is only available against a local API');
		if (!isDeviceTokenShape(token)) throw new Error('That is not a valid device token');

		const response = await fetchWithTimeout(joinApiUrl(baseUrl, 'me'), {
			headers: { Authorization: `Device ${token}` },
		});
		if (!response.ok) throw new Error(`Device token rejected (${response.status})`);
		const me = await response.json() as { id?: string; name?: string; email?: string };
		if (!me?.id) throw new Error('The API did not return a user for that token');

		// Session FIRST: switching users makes `setSession` clear the stored device token, which
		// would otherwise wipe the one just written.
		await this.setSession({ id: me.id, name: me.name ?? '', email: me.email ?? '', deviceToken: token });
		await deviceTokenService.setDeviceToken(token);
	}

	/**
	 * Assume the identity of another user (admin only)
	 * @param targetUserId The ID of the user to impersonate
	 * @param reason Optional reason for impersonation
	 */
	async assumeUser(targetUserId: string, reason?: string): Promise<void> {
		if (!this.currentSession) {
			throw new Error('No active session');
		}

		// Store the original admin session for restoration
		this.originalAdminSession = { ...this.currentSession };
		// The epoch we expect to still be on when the impersonation session is published (or, on
		// failure, restored) — any other hand-over in between (sign-out, switch) wins.
		let expectedEpoch = this.sessionEpoch;

		try {
			// Call the API to get impersonation device token
			const impersonationDeviceToken = await (await loadApiService()).assumeUser(targetUserId, reason);
			if (expectedEpoch !== this.sessionEpoch || !this.currentSession) {
				throw new Error('Session changed while starting impersonation');
			}

			// Update the current session with the impersonation device token
			this.replaceSession({
				...this.currentSession,
				deviceToken: impersonationDeviceToken,
			});
			expectedEpoch = this.sessionEpoch;

			// Store the new device token
			await deviceTokenService.setDeviceToken(impersonationDeviceToken);

			// Reload user data to get the impersonated user's information
			await this.loadStoredUser();

			// Notify listeners of the session change
			this.notifySessionListeners();
		} catch (error) {
			// Restore original session on error — unless the user signed out or switched meanwhile.
			if (expectedEpoch === this.sessionEpoch) this.replaceSession(this.originalAdminSession);
			this.originalAdminSession = null;
			throw error;
		}
	}

	/**
	 * Check if currently impersonating another user
	 */
	isImpersonating(): boolean {
		return this.originalAdminSession !== null;
	}

	/**
	 * End impersonation and return to original admin session
	 */
	async endImpersonation(): Promise<void> {
		if (!this.originalAdminSession) {
			throw new Error('Not currently impersonating');
		}

		// Restore the original admin session
		const admin = this.originalAdminSession;
		this.replaceSession(admin);
		this.originalAdminSession = null;

		// Store the original device token
		if (admin.deviceToken) {
			await deviceTokenService.setDeviceToken(admin.deviceToken);
		}

		// Reload user data to get the original admin's information
		await this.loadStoredUser();

		// Notify listeners of the session change
		this.notifySessionListeners();
	}
}

// Export singleton instance
export const authService = new AuthService();
